import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { catchError, throwError } from 'rxjs';
import { ServiceHealthService } from '@/core/services/service-health.service';

/**
 * HTTP interceptor that handles 5xx server errors.
 *
 * Behavior:
 * - On 5xx errors, adds failure to ServiceHealthService tracking window
 * - If 3+ unique endpoints fail within 30s AND liveness check fails → redirect to error page
 * - Otherwise → show toast notification
 * - Excludes /api/health/* requests to prevent infinite loops
 */
export const httpErrorInterceptor: HttpInterceptorFn = (req, next) => {
  const serviceHealth = inject(ServiceHealthService);
  const messageService = inject(MessageService);
  const router = inject(Router);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Only handle 5xx server errors
      if (error.status >= 500 && error.status < 600) {
        // Skip health check endpoints to prevent infinite loops
        if (req.url.includes('/api/health')) {
          return throwError(() => error);
        }

        // Extract endpoint path for tracking
        const endpoint = extractEndpoint(req.url);

        // Add to failure tracking window
        serviceHealth.addFailure(endpoint, error.status, req);
        serviceHealth.setLastError(error);

        // Check if this indicates a total outage
        handleServiceError(serviceHealth, messageService, router, error);
      }

      // Always re-throw the error so existing error handlers continue to work
      return throwError(() => error);
    })
  );
};

/**
 * Extract the API endpoint path from a full URL.
 * e.g., "https://localhost/api/users/search" → "/api/users/search"
 */
function extractEndpoint(url: string): string {
  try {
    const urlObj = new URL(url);
    return urlObj.pathname;
  } catch {
    // If URL parsing fails, return the original
    return url;
  }
}

/**
 * Handle service error - check failure pattern and take appropriate action.
 */
async function handleServiceError(
  serviceHealth: ServiceHealthService,
  messageService: MessageService,
  router: Router,
  error: HttpErrorResponse
): Promise<void> {
  const shouldRedirect = await serviceHealth.shouldRedirectToErrorPage();

  if (shouldRedirect) {
    // Total outage - redirect to error page
    router.navigate(['/service-unavailable']);
  } else {
    // Partial outage - show toast with retry option
    const failedCount = serviceHealth.failedEndpointCount();

    if (failedCount >= 3) {
      // Multiple failures but service is running
      messageService.add({
        key: 'serviceError',
        severity: 'warn',
        summary: 'Service Issues',
        detail: 'Multiple services are experiencing issues. Some features may be unavailable.',
        sticky: true,
        closable: true,
      });
    } else {
      // Single endpoint failure
      messageService.add({
        key: 'serviceError',
        severity: 'error',
        summary: 'Request Failed',
        detail: getErrorMessage(error),
        sticky: true,
        closable: true,
      });
    }
  }
}

/**
 * Get a user-friendly error message based on the HTTP status.
 */
function getErrorMessage(error: HttpErrorResponse): string {
  switch (error.status) {
    case 500:
      return 'An internal server error occurred. Please try again.';
    case 502:
      return 'The server is temporarily unavailable. Please try again.';
    case 503:
      return 'The service is temporarily unavailable. Please try again later.';
    case 504:
      return 'The request timed out. Please try again.';
    default:
      return 'An unexpected error occurred. Please try again.';
  }
}
